/**
 * CustomPage
 *
 * Renders non-CRUD pages using intent components from the design system.
 * Maps string component names to actual React components at runtime.
 */

'use client';

import React from 'react';
import {
  ActivityFeed,
  Card,
  ChartArea,
  ChartBar,
  ChartLine,
  ChartPie,
  ErrorState,
  KpiCard,
  LoadingState,
  Stack,
  PageHeader,
  Typography,
  // Intent components
  AnalyticsDashboard,
  SettingsSection,
  AuditLogViewer,
  NotificationCenter,
  FileAssetManager,
  OnboardingChecklist,
  FeedbackCollector,
  UsageMeter,
  ChangelogFeed,
  KeyboardShortcuts,
  ApiKeyManager,
  WebhookConfigurator,
  PermissionManager,
  SchedulingCalendar,
  MultiStepWizard,
  AdvancedSearch,
  WorkspaceSwitcher,
  PreferencesPanel,
  GuidedTour,
  MarkdownRenderer,
} from '@org/design-system';
import type { ActivityItem } from '@org/design-system';
import type { PageConfig } from '../types/app-config';
import type { EntityConfig, PageWidgetConfig } from '../types/app-config';
import { useAppConfig } from '../config/config-context';
import { createEntityHooks } from '../hooks/use-dynamic-entity';
import { buildAuthHeaders, resolveReferenceEntity } from '../utils/reference-utils';
import { clearStoredAuthToken } from '../auth/token-storage';
import { useRuntimeAuth } from '../auth/auth-context';
import { useTranslator } from '../i18n/locale-context';
import { BillingPage } from '../billing/BillingPage';
import { ComponentErrorBoundary } from '../routing/ComponentErrorBoundary';
import { usePlugin } from '../plugins/plugin-registry';

// =============================================================================
// Intent Component Registry
// =============================================================================



function MarkdownContentIntent(props: Record<string, unknown>) {
  const content = asString(props.content) || asString(props.markdown) || '';
  return <MarkdownRenderer content={content} />;
}
/* eslint-disable @typescript-eslint/no-explicit-any */
const intentRegistry: Record<string, React.ComponentType<any>> = {
  AnalyticsDashboard,
  SettingsSection,
  AuditLogViewer,
  NotificationCenter,
  FileAssetManager,
  OnboardingChecklist,
  FeedbackCollector,
  UsageMeter,
  ChangelogFeed,
  KeyboardShortcuts,
  ApiKeyManager,
  WebhookConfigurator,
  PermissionManager,
  SchedulingCalendar,
  MultiStepWizard,
  AdvancedSearch,
  WorkspaceSwitcher,
  PreferencesPanel,
  GuidedTour,
  BillingPage,
  MarkdownRenderer,
  MarkdownContentIntent,
  'markdown-content': MarkdownContentIntent,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// =============================================================================
// Component
// =============================================================================

interface CustomPageProps {
  pageConfig: PageConfig;
  apiBaseUrl: string;
}

function widgetSizeClass(size: PageWidgetConfig['size'] = 'md'): string {
  if (size === 'sm') return 'md:col-span-3 xl:col-span-3';
  if (size === 'lg') return 'md:col-span-6 xl:col-span-12';
  return 'md:col-span-6 xl:col-span-6';
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function useWidgetDataSource(
  dataSource: PageWidgetConfig['dataSource'],
  apiBaseUrl: string,
) {
  const appConfig = useAppConfig();
  const [data, setData] = React.useState<unknown>(
    typeof dataSource === 'string' ? null : dataSource,
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (typeof dataSource !== 'string') {
      setData(dataSource);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    const normalizedPath = dataSource.trim();
    if (!normalizedPath) {
      setData({});
      setIsLoading(false);
      setHasError(false);
      return;
    }

    const abortController = new AbortController();
    setIsLoading(true);
    setHasError(false);

    void (async () => {
      try {
        const response = await fetch(
          normalizedPath.startsWith('http')
            ? normalizedPath
            : `${apiBaseUrl}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`,
          {
            headers: buildAuthHeaders(appConfig.auth),
            signal: abortController.signal,
          },
        );

        if (response.status === 401) {
          clearStoredAuthToken(appConfig.auth);
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
          }
          throw new Error('Session expired. Please sign in again.');
        }

        if (!response.ok) {
          throw new Error(`Failed to load widget data: ${response.status}`);
        }

        const payload = await response.json() as unknown;
        if (!abortController.signal.aborted) {
          setData(payload);
        }
      } catch {
        if (!abortController.signal.aborted) {
          setHasError(true);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [dataSource, apiBaseUrl, appConfig.auth]);

  return { data, isLoading, hasError };
}

function WidgetCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="h-full p-4">
      <Stack gap={3}>
        <div>
          <Typography variant="h4" className="text-foreground">
            {title}
          </Typography>
          {description && (
            <Typography variant="small" className="text-muted-foreground">
              {description}
            </Typography>
          )}
        </div>
        {children}
      </Stack>
    </Card>
  );
}

function KpiWidget({
  widget,
  dataSource,
}: {
  widget: PageWidgetConfig;
  dataSource: unknown;
}) {
  const data = asRecord(dataSource);
  const metric = asString(data.metric) || String(asNumber(data.value) ?? '-');
  const subtext = asString(data.subtext);
  const trendValue = asString(data.trend);
  const trend = trendValue === 'up' || trendValue === 'down' || trendValue === 'neutral'
    ? trendValue
    : undefined;

  return (
    <KpiCard
      title={widget.title}
      metric={metric}
      subtext={subtext}
      trend={trend}
      elevated={false}
    />
  );
}

function ChartWidget({
  widget,
  dataSource,
}: {
  widget: PageWidgetConfig;
  dataSource: unknown;
}) {
  const t = useTranslator();
  const data = asRecord(dataSource);
  const chartType = asString(data.chartType) || 'bar';
  const chartData = Array.isArray(data.data) ? data.data : [];
  const index = asString(data.index) || 'name';
  const categories = Array.isArray(data.categories)
    ? data.categories.filter(value => typeof value === 'string')
    : [];
  const colors = Array.isArray(data.colors)
    ? data.colors.filter(value => typeof value === 'string')
    : undefined;
  const pieCategory = asString(data.category) || index;
  const pieValue = asString(data.valueKey) || categories[0];

  if (!chartData.length || !categories.length) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <Typography variant="small" className="text-muted-foreground">
          {t('custom.noChartData')}
        </Typography>
      </WidgetCard>
    );
  }

  const commonProps = {
    data: chartData as Record<string, unknown>[],
    index,
    categories,
    colors,
    h: 260,
  };

  return (
    <WidgetCard title={widget.title} description={widget.description}>
      {chartType === 'line' && <ChartLine {...commonProps} />}
      {chartType === 'area' && <ChartArea {...commonProps} />}
      {chartType === 'pie' && (
        <ChartPie
          data={chartData as Record<string, unknown>[]}
          category={pieCategory}
          value={pieValue}
          colors={colors}
          h={260}
        />
      )}
      {chartType !== 'line' && chartType !== 'area' && chartType !== 'pie' && (
        <ChartBar {...commonProps} />
      )}
    </WidgetCard>
  );
}

function ActivityFeedWidget({
  widget,
  dataSource,
}: {
  widget: PageWidgetConfig;
  dataSource: unknown;
}) {
  const t = useTranslator();
  const data = asRecord(dataSource);
  const items = Array.isArray(data.items) ? data.items : [];
  const normalizedItems = items.reduce<ActivityItem[]>((acc, item, index) => {
    const record = asRecord(item);
    const action = asString(record.action);
    const timestamp = asString(record.timestamp);
    if (!action || !timestamp) return acc;

    const nextItem: ActivityItem = {
      id: asString(record.id) || `${widget.id}-${index}`,
      action,
      timestamp,
    };

    const target = asString(record.target);
    if (target) {
      nextItem.target = target;
    }

    const userName = asString(record.userName);
    if (userName) {
      nextItem.user = { name: userName };
    }

    acc.push(nextItem);
    return acc;
  }, []);

  return (
    <WidgetCard title={widget.title} description={widget.description}>
      {normalizedItems.length > 0 ? (
        <ActivityFeed items={normalizedItems} variant="compact" />
      ) : (
        <Typography variant="small" className="text-muted-foreground">
          {t('custom.noActivity')}
        </Typography>
      )}
    </WidgetCard>
  );
}

function EntitySummaryWidgetContent({
  widget,
  entity,
  apiBaseUrl,
}: {
  widget: PageWidgetConfig;
  entity: EntityConfig;
  apiBaseUrl: string;
}) {
  const appConfig = useAppConfig();
  const hooks = createEntityHooks(entity, apiBaseUrl, appConfig.auth);
  const { data, isLoading, error } = hooks.useList({ page: 1, limit: 1 });

  if (isLoading) {
    return (
      <KpiCard
        title={widget.title}
        metric="..."
        subtext={widget.description || `Loading ${entity.pluralName.toLowerCase()}`}
        elevated={false}
      />
    );
  }

  if (error) {
    return (
      <KpiCard
        title={widget.title}
        metric="—"
        subtext={widget.description || `${entity.pluralName} total`}
        elevated={false}
      />
    );
  }

  return (
    <KpiCard
      title={widget.title}
      metric={String(data?.total || 0)}
      subtext={widget.description || `${entity.pluralName} total`}
      elevated={false}
    />
  );
}

function EntitySummaryWidget({
  widget,
  dataSource,
  apiBaseUrl,
}: {
  widget: PageWidgetConfig;
  dataSource: unknown;
  apiBaseUrl: string;
}) {
  const t = useTranslator();
  const appConfig = useAppConfig();
  const source = asRecord(dataSource);
  const entityRef = asString(source.entity) || asString(source.entitySlug) || asString(source.entityName);
  const entity = React.useMemo(
    () => resolveReferenceEntity(entityRef, appConfig.entities),
    [entityRef, appConfig.entities],
  );

  if (!entity) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <Typography variant="small" className="text-muted-foreground">
          {t('custom.unknownEntity')}
        </Typography>
      </WidgetCard>
    );
  }

  return (
    <EntitySummaryWidgetContent
      widget={widget}
      entity={entity}
      apiBaseUrl={apiBaseUrl}
    />
  );
}


type CustomPageWidgetPluginComponent = React.ComponentType<{
  widget: PageWidgetConfig;
  dataSource: unknown;
  apiBaseUrl: string;
}>;

function PluginWidget({
  widget,
  dataSource,
  apiBaseUrl,
}: {
  widget: PageWidgetConfig;
  dataSource: unknown;
  apiBaseUrl: string;
}) {
  const t = useTranslator();
  const source = asRecord(dataSource);
  const pluginName = asString(source.plugin) || asString(source.name) || asString(source.widget);
  const { plugin: WidgetPlugin, isLoading, error } = usePlugin<CustomPageWidgetPluginComponent>(
    'sidebarWidgets',
    pluginName,
  );

  if (!pluginName) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <Typography variant="small" className="text-muted-foreground">
          {t('custom.unknownEntity')}
        </Typography>
      </WidgetCard>
    );
  }

  if (isLoading) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <LoadingState message={t('custom.loadingWidget')} />
      </WidgetCard>
    );
  }

  if (error || !WidgetPlugin) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <Typography variant="small" className="text-destructive">
          {t('custom.widgetDataError')}
        </Typography>
      </WidgetCard>
    );
  }

  return <WidgetPlugin widget={widget} dataSource={dataSource} apiBaseUrl={apiBaseUrl} />;
}

function RuntimeWidget({
  widget,
  apiBaseUrl,
}: {
  widget: PageWidgetConfig;
  apiBaseUrl: string;
}) {
  const t = useTranslator();
  const { data, isLoading, hasError } = useWidgetDataSource(widget.dataSource, apiBaseUrl);

  if (isLoading) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <LoadingState message={t('custom.loadingWidget')} />
      </WidgetCard>
    );
  }

  if (hasError) {
    return (
      <WidgetCard title={widget.title} description={widget.description}>
        <Typography variant="small" className="text-destructive">
          {t('custom.widgetDataError')}
        </Typography>
      </WidgetCard>
    );
  }

  if (widget.type === 'plugin') {
    return <PluginWidget widget={widget} dataSource={data} apiBaseUrl={apiBaseUrl} />;
  }

  if (widget.type === 'kpi-card') {
    return <KpiWidget widget={widget} dataSource={data} />;
  }

  if (widget.type === 'chart' || widget.type === 'chart-bar' || widget.type === 'chart-line' || widget.type === 'chart-pie') {
    const normalizedData = widget.type === 'chart'
      ? data
      : {
        ...asRecord(data),
        chartType: widget.type === 'chart-line' ? 'line' : widget.type === 'chart-pie' ? 'pie' : 'bar',
      };
    return <ChartWidget widget={widget} dataSource={normalizedData} />;
  }

  if (widget.type === 'entity-summary') {
    return <EntitySummaryWidget widget={widget} dataSource={data} apiBaseUrl={apiBaseUrl} />;
  }

  if (widget.type === 'activity-feed' || widget.type === 'recent-items') {
    return <ActivityFeedWidget widget={widget} dataSource={data} />;
  }

  return <ActivityFeedWidget widget={widget} dataSource={data} />;
}

export function CustomPage({ pageConfig, apiBaseUrl }: CustomPageProps) {
  const { hasPermission } = useRuntimeAuth();
  const t = useTranslator();
  const canAccessPage = !pageConfig.requiredPermission || hasPermission(pageConfig.requiredPermission);

  if (!canAccessPage) {
    return (
      <Stack gap={6}>
        <PageHeader title={pageConfig.title} />
        <ErrorState
          title={t('custom.accessDenied.title')}
          description={t('custom.accessDenied.description')}
        />
      </Stack>
    );
  }

  if (pageConfig.widgets && pageConfig.widgets.length > 0) {
    return (
      <Stack gap={6}>
        <PageHeader title={pageConfig.title} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-12">
          {pageConfig.widgets.map(widget => (
            <div
              key={widget.id}
              className={widgetSizeClass(widget.size)}
            >
              <ComponentErrorBoundary
                errorTitle={t('custom.widgetErrorTitle')}
                errorDescription={t('custom.widgetErrorDescription', { widgetName: widget.title })}
              >
                <RuntimeWidget widget={widget} apiBaseUrl={apiBaseUrl} />
              </ComponentErrorBoundary>
            </div>
          ))}
        </div>
      </Stack>
    );
  }

  // Render intent component if specified
  if (pageConfig.intentComponent) {
    const IntentComponent = intentRegistry[pageConfig.intentComponent];

    function IntentContent() {
      if (!IntentComponent) {
        throw new Error(t('custom.unknownIntent', { componentName: pageConfig.intentComponent }));
      }
      return <IntentComponent {...(pageConfig.intentProps || {})} />;
    }

    return (
      <Stack gap={6}>
        <PageHeader title={pageConfig.title} />
        <ComponentErrorBoundary
          errorTitle={t('custom.intentErrorTitle')}
          errorDescription={IntentComponent
            ? t('custom.intentErrorDescription')
            : `${t('custom.unknownIntent', { componentName: pageConfig.intentComponent })}. ${t('custom.availableIntents', { intents: Object.keys(intentRegistry).join(', ') })}`}
        >
          <IntentContent />
        </ComponentErrorBoundary>
      </Stack>
    );
  }

  // Fallback: empty page with title
  return (
    <Stack gap={6}>
      <PageHeader title={pageConfig.title} />
      <div className="rounded-lg border border-dashed border-muted-foreground/25 p-8 text-center">
        <p className="text-muted-foreground">
          {t('custom.emptyContent')}
        </p>
      </div>
    </Stack>
  );
}
